import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { X } from 'react-feather';
import mergeRefs from 'react-merge-refs';
import { Transition, animated } from 'react-spring';
import defaultStyles from './styles/Modal.module.scss.js';
import { useModal } from './utils/useModal.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Button } from '../button/Button.js';
import { Portal } from '../portal/Portal.js';
import { omit } from '../../utils/omit.js';
import { isFunction } from '../../utils/is.js';

const Modal = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    visible,
    simple,
    title,
    children,
    cancelText,
    okText,
    closeButtonProps,
    okButtonProps,
    cancelButtonProps,
    footer,
    hideFooter = false,
    confirmLoading,
    hideCancel,
    autoFocus,
    mask = true,
    alignCenter = true,
    modalRender,
    onCancel,
    onConfirm,
    onOk,
    closable = !simple,
    dataTestId,
    theme,
    ...rest
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const {
    springConfig,
    handleOnCancel,
    modalWrapperRef,
    overlayStyle,
    inExit,
    maskClickRef,
    onClickMask,
    onConfirmModal,
    loading,
    contentWrapper
  } = useModal({
    visible,
    confirmLoading,
    onCancel,
    mask,
    onConfirm,
    onOk
  });
  const renderFooter = () => {
    if (footer === null) return null;
    const cancelButtonNode = /*#__PURE__*/jsx(Button, {
      "data-type": "cancel",
      onClick: onCancel,
      ...cancelButtonProps,
      children: cancelText || 'Cancel'
    });

    // todo: Fix modal button types
    const okButtonNode =
    /*#__PURE__*/
    // @ts-ignore
    jsx(Button, {
      "data-type": "ok",
      loading: loading,
      onClick: onConfirmModal,
      type: "button",
      ...okButtonProps,
      children: okText || 'Ok'
    });
    const footerContent = footer || /*#__PURE__*/jsxs(Fragment, {
      children: [!hideCancel && cancelButtonNode, okButtonNode]
    });
    return !hideFooter ? /*#__PURE__*/jsx("div", {
      className: styles.footer,
      children: footerContent
    }) : null;
  };
  const element = /*#__PURE__*/jsxs(Fragment, {
    children: [title && /*#__PURE__*/jsx("div", {
      className: styles.header,
      children: /*#__PURE__*/jsx("div", {
        className: styles.title,
        children: title
      })
    }), /*#__PURE__*/jsx("div", {
      ref: contentWrapper,
      className: styles.content,
      children: children
    }), renderFooter(), closable && /*#__PURE__*/jsx(Button, {
      theme: {
        root: styles.close_button
      },
      shape: "circle",
      size: "sm",
      variant: "ghost",
      status: "secondary",
      onClick: onCancel,
      ...closeButtonProps,
      children: /*#__PURE__*/jsx(X, {})
    })]
  });
  const modalDom = /*#__PURE__*/jsx(Fragment, {
    children: element
  });
  return /*#__PURE__*/jsx(Portal, {
    children: /*#__PURE__*/jsx(Transition, {
      native: true,
      items: visible,
      from: springConfig.from,
      enter: springConfig.to,
      leave: springConfig.from,
      children: (transitionStyles, item) => item && /*#__PURE__*/jsxs("div", {
        className: clsx(styles.root, {
          'no-mask': !mask,
          'wrapper-hide': !visible
        }),
        children: [mask ? /*#__PURE__*/jsx(animated.div, {
          style: overlayStyle,
          className: styles.mask,
          onClick: () => {
            if (!inExit.current) {
              handleOnCancel();
            }
          }
        }) : null, /*#__PURE__*/jsx(animated.div, {
          role: "dialog",
          "aria-hidden": visible,
          ...omit(rest, ['content', 'icon', 'showIcon', 'isNotice', 'noticeType', 'onCancel', 'onOk', 'onConfirm', 'closable', 'prefixCls']),
          ref: mergeRefs([ref, modalWrapperRef]),
          className: styles.modal,
          "data-test-id": dataTestId,
          style: {
            overflow: !visible ? 'hidden' : '',
            ...transitionStyles
          },
          onMouseDown: e => {
            maskClickRef.current = e.target === e.currentTarget;
          },
          onClick: onClickMask,
          children: /*#__PURE__*/React.cloneElement(isFunction(modalRender) ? modalRender(modalDom) : modalDom, {
            onMouseDown: () => {
              maskClickRef.current = false;
            },
            onMouseUp: () => {
              maskClickRef.current = false;
            }
          })
        })]
      })
    })
  });
});
Modal.displayName = 'Modal';

export { Modal };
